import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/styles';
import { Card, CardHeader, CardContent } from '@material-ui/core';
import Accordion from '@material-ui/core/ExpansionPanel';
import AccordionSummary from '@material-ui/core/ExpansionPanelSummary';
import ExpandMoreIcon from '@material-ui/icons/ExpandMoreOutlined';

import styles from 'styles/admin-detail.module.scss';
import { scroller } from 'react-scroll';
import Scroll from 'react-scroll';

import AsyncButton from 'utils/AsyncButton';
import { ErrorList } from 'components';
import {
  createChatInputErrorMessage,
  createFreeChatInputErrorMessage,
} from 'utils/validationMessage';

import ChatInputEdit from './ChatInputEdit';
import ChatHistory from './ChatHistory';
import ChatInputConfirm from './ChatInputConfirm';
import FreeChatInput from './FreeChatInput';
import FreeChatInputConfirm from './FreeChatInputConfirm';
import ModalWrapper from 'utils/ModalWrapper';
import { isPermitted } from 'utils/utils';

import { currentUserSelector } from 'store/current_user';
import { NON_FINANCIAL_FORM_TYPE } from "const";

const useStyles = makeStyles(() => ({
  root: { marginBottom: 15 },
  blockLabel: {
    fontSize: 20,
    marginTop: 25,
    marginBottom: 8,
    height: 40,
  },
}));

const ChatInputField = (props) => {
  const { className } = props;
  const {
    detailData,
    chatInputFormik,
    freeChatInputFormik,
    isChatSubmitInProgress,
    isFreeChatSubmitInProgress,
  } = props;
  const classes = useStyles();
  // ステータスが、「6: チャット（承諾済）」「7: チャット（OUT要）」「8: 反映待ち」「9: 完了」以外の場合は送信可能
  const canSendChat =
    ['6', '7', '8', '9'].indexOf(detailData.chatInput?.responseStatus) === -1;
  const [
    shouldDisplayChatErrorMessage,
    setShouldDisplayChatErrorMessage,
  ] = useState(false);
  const [
    shouldDisplayFreeChatErrorMessage,
    setShouldDisplayFreeChatErrorMessage,
  ] = useState(false);

  const [confirmModal, setConfirmModal] = useState(false);
  const [confirmFreeLineModal, setConfirmFreeLineModal] = useState(false);

  const scrollToChatInputErrorMessage = () => {
    scroller.scrollTo('chat_input_error_message', {
      duration: 800,
      delay: 0,
      smooth: 'easeInOutQuart',
      offset: -70,
    });
  };

  const scrollToFreeChatInputErrorMessage = () => {
    scroller.scrollTo('free_chat_input_error_message', {
      duration: 800,
      delay: 0,
      smooth: 'easeInOutQuart',
      offset: -70,
    });
  };

  const currentUser = useSelector(currentUserSelector)

  return (
    <>
      {(detailData.vehicle?.id ||
        detailData.nenrei?.id ||
        detailData.purpose?.id) && (
        <Card className={clsx(classes.root)}>
          <CardHeader
            title={
              <p>
                <span className={styles.LineStyle}>LINE</span>
                チャット 承諾依頼メッセージ
                {!canSendChat && (
                  <span className={styles.cannotSendMessage}>
                    （現在のステータスでは送信できません）
                  </span>
                )}
              </p>
            }
            className={styles.lineChatColor}
          />
          <CardContent className={styles.cardContent}>
            <Scroll.Element name='chat_input_error_message'>
              <ErrorList
                errors={createChatInputErrorMessage(chatInputFormik.errors)}
                isError={shouldDisplayChatErrorMessage}
              />
            </Scroll.Element>
            <ChatInputEdit
              detailData={detailData}
              chatInputFormik={chatInputFormik}
            />
            {!canSendChat && (
              <p
                className={clsx(styles.cannotSendMessage, styles.displayCenter)}
              >
                現在のステータスでは送信できません。
                <br />
                ステータスの変更を行なった後で送信してください。
              </p>
            )}
            <div className={styles.displayRight}>
              <AsyncButton
                className={styles.confirmButton}
                disabled={!canSendChat || !isPermitted(currentUser, detailData.lockedBy)}
                type='button'
                color='primary'
                variant='contained'
                onClick={async () => {
                  const errors = await chatInputFormik.validateForm();
                  setShouldDisplayChatErrorMessage(true);
                  if (Object.keys(errors).length === 0) {
                    setConfirmModal(true);
                  } else {
                    // 一度もフォームをクリックしていないと、chatInputFormikのtouchedがtrueにならず、
                    // 入力欄が赤くならないので、ここでhandleSubmit()する。
                    // このelse句の中にはerrorがある時しか通らないので、送信はされない。
                    chatInputFormik.handleSubmit();
                    scrollToChatInputErrorMessage();
                  }
                }}
              >
                送信内容確認
              </AsyncButton>
            </div>
          </CardContent>
        </Card>
      )}
      <ModalWrapper
        open={confirmModal}
        handleClose={() => setConfirmModal(false)}
        scroll={true}
        content={
          <ChatInputConfirm
            chatInputFormik={chatInputFormik}
            detailData={detailData}
            isChatSubmitInProgress={isChatSubmitInProgress}
            closeModal={() => setConfirmModal(false)}
            canSend={true}
          />
        }
      />
      <Accordion className={clsx(classes.root)}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          className={clsx(styles.lineChatColor)}
        >
          <span className={styles.lineStyle}>LINE</span>
          チャット フリーメッセージ
        </AccordionSummary>
        <CardContent className={styles.cardContent}>
          <Scroll.Element name='free_chat_input_error_message'>
            <ErrorList
              errors={createFreeChatInputErrorMessage(
                freeChatInputFormik.errors
              )}
              isError={shouldDisplayFreeChatErrorMessage}
            />
          </Scroll.Element>
          <FreeChatInput freeChatInputFormik={freeChatInputFormik} lockedBy={detailData.lockedBy} />
          <div className={styles.displayRight}>
            <AsyncButton
              className={styles.confirmButton}
              type='button'
              color='primary'
              variant='contained'
              disabled={!isPermitted(currentUser, detailData.lockedBy)}
              onClick={() => {
                setShouldDisplayFreeChatErrorMessage(true);
                if (Object.keys(freeChatInputFormik.errors).length > 0) {
                  scrollToFreeChatInputErrorMessage();
                } else if (freeChatInputFormik.values.freeMessage !== '' && Object.keys(NON_FINANCIAL_FORM_TYPE).includes(String(freeChatInputFormik.values.type))) {
                  setConfirmFreeLineModal(true);
                }
              }}
            >
              送信内容確認
            </AsyncButton>
          </div>
        </CardContent>
      </Accordion>
      <ModalWrapper
        open={confirmFreeLineModal}
        handleClose={() => setConfirmFreeLineModal(false)}
        scroll={true}
        content={
          <FreeChatInputConfirm
            freeChatInputFormik={freeChatInputFormik}
            closeModal={() => setConfirmFreeLineModal(false)}
            isFreeChatSubmitInProgress={isFreeChatSubmitInProgress}
            canSend={true}
          />
        }
      />
      <Card className={clsx(classes.root, className)}>
        <CardHeader
          title={
            <p>
              <span className={styles.lineStyle}>LINE</span>
              対応履歴
            </p>
          }
          className={styles.lineChatColor}
        />
        <ChatHistory
          detailData={detailData}
        />
      </Card>
    </>
  );
};

export default ChatInputField;
